import type { Feature, Geometry, Point, Position } from 'geojson'
import turfArea from '@turf/area'
import turfLength from '@turf/length'
import turfCenterOfMass from '@turf/center-of-mass'
import turfMidpoint from '@turf/midpoint'
import turfPointOnFeature from '@turf/point-on-feature'

export type MeasurementUnits = 'metric' | 'imperial'

export const DEFAULT_UNITS: MeasurementUnits = 'imperial'

// -----------------------------------------------------------------------------
// Conversion constants (exact where possible)
// -----------------------------------------------------------------------------
const METERS_PER_FOOT = 0.3048
const METERS_PER_MILE = 1609.344
const SQM_PER_SQFT = 0.09290304
const SQM_PER_ACRE = 4046.8564224
const SQM_PER_SQMI = 2_589_988.110336

// -----------------------------------------------------------------------------
// Formatters — return a concise human-readable string with auto-scaled units.
// -----------------------------------------------------------------------------

const formatNumber = (value: number, decimals = 2): string => {
  // Use Intl to get thousands separators while keeping fixed decimals.
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/** Format a length given in meters according to the chosen system. */
export const formatLength = (
  meters: number,
  units: MeasurementUnits
): string => {
  if (!Number.isFinite(meters) || meters < 0) return '—'
  if (units === 'metric') {
    if (meters < 1000) return `${formatNumber(meters, meters < 10 ? 2 : 1)} m`
    return `${formatNumber(meters / 1000, 2)} km`
  }
  const feet = meters / METERS_PER_FOOT
  if (feet < 5280) return `${formatNumber(feet, feet < 10 ? 2 : 1)} ft`
  return `${formatNumber(meters / METERS_PER_MILE, 2)} mi`
}

/** Format an area given in square meters according to the chosen system. */
export const formatArea = (
  squareMeters: number,
  units: MeasurementUnits
): string => {
  if (!Number.isFinite(squareMeters) || squareMeters < 0) return '—'
  if (units === 'metric') {
    if (squareMeters < 10_000) return `${formatNumber(squareMeters, 2)} m²`
    if (squareMeters < 1_000_000) {
      return `${formatNumber(squareMeters / 10_000, 2)} ha`
    }
    return `${formatNumber(squareMeters / 1_000_000, 2)} km²`
  }
  const sqFt = squareMeters / SQM_PER_SQFT
  if (sqFt < 43_560) return `${formatNumber(sqFt, 2)} ft²`
  const acres = squareMeters / SQM_PER_ACRE
  if (acres < 640) return `${formatNumber(acres, 2)} ac`
  return `${formatNumber(squareMeters / SQM_PER_SQMI, 2)} mi²`
}

/** Format a lat/lng coordinate pair — 6 decimals ≈ 11 cm resolution. */
export const formatCoord = (lng: number, lat: number): string =>
  `${lat.toFixed(6)}, ${lng.toFixed(6)}`

// -----------------------------------------------------------------------------
// Per-geometry measurement computation
// -----------------------------------------------------------------------------

export interface MeasurementRow {
  label: string
  value: string
}

export interface MeasurementResult {
  /** Short human label for the geometry (e.g. "Polygon", "Line", "Point"). */
  geomLabel: string
  rows: MeasurementRow[]
}

export interface MeasurementSegment {
  start: Position
  end: Position
  midpoint: Position
  meters: number
}

/** Unit-agnostic geometry measurements shared by summary UI and map labels. */
export interface GeometryMeasurementDetails {
  geomLabel: string
  geometryType: Geometry['type']
  coordinate?: Position
  coordinateCount?: number
  segments: MeasurementSegment[]
  totalLengthMeters?: number
  perimeterMeters?: number
  areaSquareMeters?: number
  totalAnchor?: Position
  summaryAnchor?: Position
}

export type MeasurementLabelKind =
  | 'coordinate'
  | 'segment'
  | 'total'
  | 'polygon-summary'

export type MeasurementLabelFeature = Feature<
  Point,
  {
    measurement: string
    measurementSecondLine?: string
    measurementKind: MeasurementLabelKind
    isActive: boolean
    measurementLengthMeters?: number
    measurementAreaSquareMeters?: number
  }
>

export interface MeasurementLabelOptions {
  coordinates?: boolean
  latitude?: boolean
  longitude?: boolean
  segments?: boolean
  lineTotal?: boolean
  polygonSummary?: boolean
  polygonPerimeter?: boolean
  polygonArea?: boolean
  maxSegmentLabels?: number
}

const DEFAULT_MAX_SEGMENT_LABELS = Number.POSITIVE_INFINITY

const geomLabelFor = (type: string): string => {
  switch (type) {
    case 'Polygon':
    case 'MultiPolygon':
      return 'Polygon'
    case 'LineString':
    case 'MultiLineString':
      return 'Line'
    case 'Point':
      return 'Point'
    case 'MultiPoint':
      return 'Points'
    default:
      return type
  }
}

const isValidPosition = (
  position: Position | undefined
): position is Position =>
  Array.isArray(position) &&
  position.length >= 2 &&
  Number.isFinite(position[0]) &&
  Number.isFinite(position[1])

const positionsMatch = (first: Position, second: Position): boolean =>
  first[0] === second[0] && first[1] === second[1]

const coordinateSequencesFor = (geometry: Geometry): Position[][] => {
  switch (geometry.type) {
    case 'LineString':
      return [geometry.coordinates]
    case 'MultiLineString':
      return geometry.coordinates
    case 'Polygon':
      return geometry.coordinates
    case 'MultiPolygon':
      return geometry.coordinates.flat()
    default:
      return []
  }
}

const buildSegments = (geometry: Geometry): MeasurementSegment[] =>
  coordinateSequencesFor(geometry).flatMap(sequence => {
    const segments: MeasurementSegment[] = []
    for (let index = 1; index < sequence.length; index += 1) {
      const start = sequence[index - 1]
      const end = sequence[index]
      if (
        !isValidPosition(start) ||
        !isValidPosition(end) ||
        positionsMatch(start, end)
      ) {
        continue
      }

      const segment: Feature = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [start, end] },
        properties: {},
      }
      const meters = turfLength(segment as never, { units: 'meters' })
      if (!Number.isFinite(meters) || meters <= 0) continue

      const midpointFeature = turfMidpoint(start, end)
      const midpoint = midpointFeature.geometry?.coordinates
      if (!isValidPosition(midpoint)) continue
      segments.push({ start, end, midpoint, meters })
    }
    return segments
  })

const lastValidPosition = (geometry: Geometry): Position | undefined => {
  const sequences = coordinateSequencesFor(geometry)
  for (
    let sequenceIndex = sequences.length - 1;
    sequenceIndex >= 0;
    sequenceIndex -= 1
  ) {
    const sequence = sequences[sequenceIndex]
    for (
      let positionIndex = sequence.length - 1;
      positionIndex >= 0;
      positionIndex -= 1
    ) {
      const position = sequence[positionIndex]
      if (isValidPosition(position)) return position
    }
  }
  return undefined
}

const polygonSummaryAnchor = (
  feature: Feature<Geometry>
): Position | undefined => {
  try {
    const center = turfCenterOfMass(feature as never).geometry.coordinates
    if (isValidPosition(center)) return center
  } catch {
    // In-progress polygons can be temporarily invalid; fall through.
  }

  try {
    const point = turfPointOnFeature(feature as never).geometry?.coordinates
    return isValidPosition(point) ? point : undefined
  } catch {
    return undefined
  }
}

/** Compute a reusable numeric model for a GeoJSON geometry. */
export const computeMeasurementDetails = (
  geometry: Geometry | null | undefined
): GeometryMeasurementDetails | null => {
  if (!geometry) return null

  const feature: Feature<Geometry> = {
    type: 'Feature',
    geometry,
    properties: {},
  }
  const base: GeometryMeasurementDetails = {
    geomLabel: geomLabelFor(geometry.type),
    geometryType: geometry.type,
    segments: [],
  }

  switch (geometry.type) {
    case 'Point':
      return isValidPosition(geometry.coordinates)
        ? { ...base, coordinate: geometry.coordinates, coordinateCount: 1 }
        : base
    case 'MultiPoint':
      return {
        ...base,
        coordinate: geometry.coordinates.find(isValidPosition),
        coordinateCount: geometry.coordinates.filter(isValidPosition).length,
      }
    case 'LineString':
    case 'MultiLineString': {
      const segments = buildSegments(geometry)
      return {
        ...base,
        segments,
        totalLengthMeters: segments.reduce(
          (total, segment) => total + segment.meters,
          0
        ),
        totalAnchor: lastValidPosition(geometry),
      }
    }
    case 'Polygon':
    case 'MultiPolygon': {
      const segments = buildSegments(geometry)
      let areaSquareMeters: number | undefined
      try {
        const area = turfArea(feature as never)
        areaSquareMeters = Number.isFinite(area) ? area : undefined
      } catch {
        // Terra emits provisional polygons before they form a valid ring.
      }
      return {
        ...base,
        segments,
        perimeterMeters: segments.reduce(
          (total, segment) => total + segment.meters,
          0
        ),
        areaSquareMeters,
        summaryAnchor: polygonSummaryAnchor(feature),
      }
    }
    default:
      return base
  }
}

export const formatMeasurementDetails = (
  details: GeometryMeasurementDetails | null,
  units: MeasurementUnits
): MeasurementResult => {
  if (!details) return { geomLabel: 'Feature', rows: [] }

  switch (details.geometryType) {
    case 'Polygon':
    case 'MultiPolygon':
      return {
        geomLabel: details.geomLabel,
        rows: [
          ...(details.perimeterMeters == null
            ? []
            : [
                {
                  label: 'Perimeter',
                  value: formatLength(details.perimeterMeters, units),
                },
              ]),
          ...(details.areaSquareMeters == null
            ? []
            : [
                {
                  label: 'Area',
                  value: formatArea(details.areaSquareMeters, units),
                },
              ]),
        ],
      }
    case 'LineString':
    case 'MultiLineString':
      return {
        geomLabel: details.geomLabel,
        rows:
          details.totalLengthMeters == null
            ? []
            : [
                {
                  label: 'Total Length',
                  value: formatLength(details.totalLengthMeters, units),
                },
              ],
      }
    case 'Point':
    case 'MultiPoint': {
      const [lng, lat] = details.coordinate ?? []
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        return { geomLabel: details.geomLabel, rows: [] }
      }
      return {
        geomLabel: details.geomLabel,
        rows: [
          {
            label: 'Latitude',
            value: lat.toFixed(6),
          },
          {
            label: 'Longitude',
            value: `${lng.toFixed(6)}${
              details.geometryType === 'MultiPoint' &&
              (details.coordinateCount ?? 0) > 1
                ? ` (+${(details.coordinateCount ?? 1) - 1} more)`
                : ''
            }`,
          },
        ],
      }
    }
    default:
      return { geomLabel: details.geomLabel, rows: [] }
  }
}

const labelPoint = (
  coordinates: Position,
  properties: MeasurementLabelFeature['properties']
): MeasurementLabelFeature => ({
  type: 'Feature',
  geometry: { type: 'Point', coordinates },
  properties,
})

/** Reformat persisted label properties from their unit-agnostic values. */
export const withFormattedMeasurementLabelUnits = (
  properties: Record<string, unknown>,
  units: MeasurementUnits
): Record<string, unknown> => {
  const lengthMeters = properties.measurementLengthMeters
  const areaSquareMeters = properties.measurementAreaSquareMeters
  let measurement: string | undefined
  let measurementSecondLine: string | undefined

  switch (properties.measurementKind) {
    case 'segment':
      if (typeof lengthMeters !== 'number') return properties
      measurement = formatLength(lengthMeters, units)
      break
    case 'total':
      if (typeof lengthMeters !== 'number') return properties
      measurement = `Total: ${formatLength(lengthMeters, units)}`
      break
    case 'polygon-summary': {
      const summaryLines = [
        ...(typeof lengthMeters === 'number'
          ? [`Perimeter: ${formatLength(lengthMeters, units)}`]
          : []),
        ...(typeof areaSquareMeters === 'number'
          ? [`Area: ${formatArea(areaSquareMeters, units)}`]
          : []),
      ]
      if (summaryLines.length === 0) return properties
      ;[measurement, measurementSecondLine] = summaryLines
      break
    }
    default:
      return properties
  }

  if (
    properties.measurement === measurement &&
    properties.measurementSecondLine === measurementSecondLine
  ) {
    return properties
  }

  return {
    ...properties,
    measurement,
    ...(measurementSecondLine ? { measurementSecondLine } : {}),
  }
}

/** Build MapLibre-ready label points from the same model as the compact UI. */
export const buildMeasurementLabelFeatures = (
  geometry: Geometry | null | undefined,
  units: MeasurementUnits,
  isActive: boolean,
  options: MeasurementLabelOptions = {}
): MeasurementLabelFeature[] => {
  const details = computeMeasurementDetails(geometry)
  if (!details) return []
  const coordinates = options.coordinates ?? true
  const latitude = options.latitude ?? coordinates
  const longitude = options.longitude ?? coordinates
  const segments = options.segments ?? true
  const lineTotal = options.lineTotal ?? true
  const polygonSummary = options.polygonSummary ?? true
  const polygonPerimeter = options.polygonPerimeter ?? polygonSummary
  const polygonArea = options.polygonArea ?? polygonSummary

  if (
    (details.geometryType === 'Point' ||
      details.geometryType === 'MultiPoint') &&
    details.coordinate &&
    (latitude || longitude)
  ) {
    const [lng, lat] = details.coordinate
    const coordinateLines = [
      ...(latitude ? [`Lat: ${lat.toFixed(6)}`] : []),
      ...(longitude ? [`Long: ${lng.toFixed(6)}`] : []),
    ]
    return [
      labelPoint(details.coordinate, {
        measurement: coordinateLines[0],
        ...(coordinateLines[1]
          ? { measurementSecondLine: coordinateLines[1] }
          : {}),
        measurementKind: 'coordinate',
        isActive,
      }),
    ]
  }

  const maxSegmentLabels =
    Number.isFinite(options.maxSegmentLabels) &&
    (options.maxSegmentLabels ?? 0) > 0
      ? Math.floor(options.maxSegmentLabels ?? DEFAULT_MAX_SEGMENT_LABELS)
      : DEFAULT_MAX_SEGMENT_LABELS
  const segmentStep = Math.max(
    1,
    Math.ceil(details.segments.length / maxSegmentLabels)
  )
  const visibleSegments = segments
    ? details.segments.filter((_segment, index) => index % segmentStep === 0)
    : []
  const segmentLabels = visibleSegments.map(segment =>
    labelPoint(segment.midpoint, {
      measurement: formatLength(segment.meters, units),
      measurementKind: 'segment',
      isActive,
      measurementLengthMeters: segment.meters,
    })
  )

  if (
    (details.geometryType === 'LineString' ||
      details.geometryType === 'MultiLineString') &&
    details.totalAnchor &&
    details.totalLengthMeters != null &&
    lineTotal
  ) {
    segmentLabels.push(
      labelPoint(details.totalAnchor, {
        measurement: `Total: ${formatLength(details.totalLengthMeters, units)}`,
        measurementKind: 'total',
        isActive,
        measurementLengthMeters: details.totalLengthMeters,
      })
    )
  }

  if (
    (details.geometryType === 'Polygon' ||
      details.geometryType === 'MultiPolygon') &&
    details.summaryAnchor &&
    ((polygonPerimeter && details.perimeterMeters != null) ||
      (polygonArea && details.areaSquareMeters != null))
  ) {
    const summaryLines = [
      ...(polygonPerimeter && details.perimeterMeters != null
        ? [`Perimeter: ${formatLength(details.perimeterMeters, units)}`]
        : []),
      ...(polygonArea && details.areaSquareMeters != null
        ? [`Area: ${formatArea(details.areaSquareMeters, units)}`]
        : []),
    ]
    segmentLabels.push(
      labelPoint(details.summaryAnchor, {
        measurement: summaryLines[0],
        ...(summaryLines[1] ? { measurementSecondLine: summaryLines[1] } : {}),
        measurementKind: 'polygon-summary',
        isActive,
        ...(polygonPerimeter && details.perimeterMeters != null
          ? { measurementLengthMeters: details.perimeterMeters }
          : {}),
        ...(polygonArea && details.areaSquareMeters != null
          ? { measurementAreaSquareMeters: details.areaSquareMeters }
          : {}),
      })
    )
  }

  return segmentLabels
}

/**
 * Compute the measurements we surface for a given GeoJSON geometry. Handles
 * `Polygon`, `LineString`, `Point`, their `Multi*` counterparts; anything
 * else (e.g. `GeometryCollection`) returns an empty row list so the caller
 * can show an "unsupported" hint.
 */
export const computeMeasurements = (
  geometry: Geometry | null | undefined,
  units: MeasurementUnits
): MeasurementResult =>
  formatMeasurementDetails(computeMeasurementDetails(geometry), units)
